import {
  getCompoundEntityRef,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { ErrorPanel, Link, Table } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import {
  catalogApiRef,
  EntityRefLink,
  useEntity,
} from '@backstage/plugin-catalog-react';
import { techInsightsApiRef } from '@backstage-community/plugin-tech-insights-react';
import { Box, Typography } from '@material-ui/core';
import useAsync from 'react-use/lib/useAsync';
import { groupByStatus } from '../../techInsights/EntityScorecardCard';
import { entityTier } from '../ServiceTier';
import { scoreColor, Strip, Tile } from '../overview/StatsStrip';

const NO_DASHBOARD = 'newrelic.com/no-dashboard';

const dashboardOf = (links: { url: string; icon?: string }[] = []) =>
  links.find(link => link.icon === 'dashboard')?.url;

/** The services a team owns: a summary of how they are doing, then each one's tier, scorecard and dashboard. */
export const TeamServices = () => {
  const { entity } = useEntity();
  const catalogApi = useApi(catalogApiRef);
  const techInsights = useApi(techInsightsApiRef);
  const teamRef = stringifyEntityRef(entity);

  const { value, loading, error } = useAsync(async () => {
    const { items } = await catalogApi.getEntities({
      filter: {
        kind: 'Component',
        'spec.type': 'service',
        'relations.ownedBy': teamRef,
      },
    });
    const responses = items.length
      ? await techInsights.runBulkChecks(items.map(getCompoundEntityRef))
      : [];
    return items.map((service, i) => {
      const grouped = groupByStatus(techInsights, responses[i]?.results ?? []);
      return {
        service,
        passing: grouped.passing.length,
        failing: grouped.failing.length,
        dashboard: dashboardOf(service.metadata.links),
        reason: service.metadata.annotations?.[NO_DASHBOARD],
      };
    });
  }, [teamRef]);

  if (error) return <ErrorPanel error={error} />;
  const rows = value ?? [];
  const passing = rows.reduce((n, r) => n + r.passing, 0);
  const total = passing + rows.reduce((n, r) => n + r.failing, 0);
  const shown = (n: number) => (loading ? '-' : n);

  return (
    <>
      <Strip>
        <Tile label="Services">{shown(rows.length)}</Tile>
        <Tile label="Tier 1">
          {shown(rows.filter(r => entityTier(r.service) === '1').length)}
        </Tile>
        <Tile label="Checks passing">
          {loading || !total ? (
            '-'
          ) : (
            <span style={{ color: scoreColor(total - passing) }}>
              {passing} of {total}
            </span>
          )}
        </Tile>
        <Tile label="No dashboard">
          {shown(rows.filter(r => !r.dashboard && !r.reason).length)}
        </Tile>
      </Strip>
      <Table
        title="Services"
        isLoading={loading}
        options={{ paging: false, search: false }}
        data={rows}
        columns={[
          {
            title: 'Service',
            customSort: (a, b) =>
              a.service.metadata.name.localeCompare(b.service.metadata.name),
            render: ({ service }) => (
              <EntityRefLink entityRef={service} defaultKind="Component" />
            ),
          },
          {
            title: 'Tier',
            defaultSort: 'asc',
            customSort: (a, b) =>
              (entityTier(a.service) ?? '9').localeCompare(
                entityTier(b.service) ?? '9',
                undefined,
                { numeric: true },
              ) || b.failing - a.failing,
            render: ({ service }) => {
              const tier = entityTier(service);
              return tier ? `T${tier}` : <em>Not set</em>;
            },
          },
          {
            title: 'Scorecard',
            customSort: (a, b) => b.failing - a.failing,
            render: ({ passing: ok, failing }) => (
              <Box minWidth={140}>
                <Box height={6} borderRadius={3} bgcolor="action.hover">
                  <Box
                    height={6}
                    borderRadius={3}
                    bgcolor={scoreColor(failing)}
                    width={`${ok + failing ? (100 * ok) / (ok + failing) : 0}%`}
                  />
                </Box>
                <Typography variant="caption" color="textSecondary">
                  {failing ? `${failing} failing` : 'All passing'}
                </Typography>
              </Box>
            ),
          },
          {
            title: 'Dashboard',
            sorting: false,
            render: ({ dashboard, reason }) => {
              if (dashboard) return <Link to={dashboard}>New Relic</Link>;
              return reason ? (
                <Typography variant="caption" color="textSecondary">
                  None: {reason}
                </Typography>
              ) : (
                <span style={{ color: '#ed6c02' }}>Missing</span>
              );
            },
          },
        ]}
      />
    </>
  );
};
